import { useMemo, useState } from 'react';
import { compact, count } from '../lib/money';
import type { UsageTotals } from '../lib/rpc-client';

export type UsageMetric = 'served_units' | 'served_requests';

const METRIC_LABEL: Record<UsageMetric, string> = {
  served_units: 'units',
  served_requests: 'requests',
};

/** Every day from `from` to `to` (YYYY-MM-DD, inclusive), so empty days show as gaps. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/** A clean axis maximum (1, 2, 2.5, 5 × 10^n) at or above `v`. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (v <= m * exp) return m * exp;
  }
  return 10 * exp;
}

const W = 720;
const H = 220;
const PAD = { top: 12, right: 8, bottom: 26, left: 44 };
const MAX_BAR = 24;
const GAP = 2;

/**
 * Daily usage as columns: one series, so no legend box (the heading names
 * it). Columns grow from one baseline with a 4px rounded top; hovering or
 * focusing a column shows that day's numbers. The by-day table below the chart
 * carries every value.
 */
export function UsageChart({
  days,
  byDay,
  metric,
}: {
  days: string[];
  byDay: Record<string, UsageTotals>;
  metric: UsageMetric;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const values = useMemo(() => days.map((d) => byDay[d]?.[metric] ?? 0), [days, byDay, metric]);
  const max = niceMax(Math.max(0, ...values));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / Math.max(1, days.length);
  const barW = Math.max(2, Math.min(MAX_BAR, slot - GAP));
  const ticks = [0, max / 2, max];
  const labelEvery = Math.ceil(days.length / 8);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const hovered = hover != null ? days[hover] : null;

  return (
    <div className="rpc-chart" data-testid="usage-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${METRIC_LABEL[metric]} per day`}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(t)}
              y2={y(t)}
              className="rpc-chart-grid"
            />
            <text x={PAD.left - 8} y={y(t)} className="rpc-chart-tick" textAnchor="end" dominantBaseline="middle">
              {compact(t)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const v = values[i];
          const x = PAD.left + i * slot + (slot - barW) / 2;
          const top = y(v);
          const h = PAD.top + plotH - top;
          const r = Math.min(4, barW / 2, h);
          return (
            <g key={d}>
              {/* Hit target: the whole slot, taller than the column. */}
              <rect
                x={PAD.left + i * slot}
                y={PAD.top}
                width={slot}
                height={plotH}
                className="rpc-chart-hit"
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                tabIndex={0}
                aria-label={`${d}: ${count(v)} ${METRIC_LABEL[metric]}`}
              />
              {v > 0 && (
                <path
                  className={`rpc-chart-bar${hover === i ? ' is-hover' : ''}`}
                  d={`M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${top + h} Z`}
                />
              )}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 8} className="rpc-chart-tick" textAnchor="middle">
                  {d.slice(5)}
                </text>
              )}
            </g>
          );
        })}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          className="rpc-chart-axis"
        />
      </svg>
      {hovered && (
        <div className="rpc-chart-tip" role="status" data-testid="usage-tip">
          <strong>{hovered}</strong>
          <span>{count(byDay[hovered]?.served_requests ?? 0)} requests</span>
          <span>{count(byDay[hovered]?.served_units ?? 0)} units</span>
          {(byDay[hovered]?.refused ?? 0) > 0 && <span>{count(byDay[hovered].refused)} refused</span>}
        </div>
      )}
    </div>
  );
}
